import React from 'react';
import { Archive, ChevronRight } from 'lucide-react';
import type { TenderRow } from '../types';

const qty = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 });
const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

/**
 * Alt-Entwurf aus der Zeit vor der Akte mit Vorgang: Positionen lagen als JSON in `offer_draft`.
 * Nur noch lesbar; zum Weiterarbeiten das LV in den Vorgang importieren.
 */
export const TenderLegacyDraftPanel: React.FC<{ rows: TenderRow[]; hasOffering: boolean }> = ({ rows, hasOffering }) => {
  const [open, setOpen] = React.useState(!hasOffering);
  if (rows.length === 0) return null;
  return (
    <section className="rounded-lg border border-amber-200 bg-amber-50/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left"
        aria-expanded={open}
      >
        <ChevronRight className={`w-4 h-4 text-amber-700 transition-transform ${open ? 'rotate-90' : ''}`} />
        <Archive className="w-4 h-4 text-amber-700" />
        <span className="text-sm font-semibold text-amber-900">Alter Entwurf (schreibgeschützt)</span>
        <span className="text-sm text-amber-800">· {rows.length} Positionen</span>
      </button>
      {open && (
        <div className="px-4 pb-4">
          <p className="text-sm text-amber-800 mb-2">
            Diese Positionen stammen aus der früheren Ausschreibungsansicht. Sie werden nicht mehr bearbeitet.
            {hasOffering
              ? ' Das aktuelle LV liegt im Vorgang.'
              : ' Importiere das LV aus der GAEB-Datei, um im Angebotseditor zu kalkulieren.'}
          </p>
          <div className="overflow-x-auto rounded-md border border-amber-200 bg-app-surface">
            <table className="min-w-full text-sm">
              <thead className="bg-app-surface-muted text-app-text-subtle">
                <tr>
                  <th className="px-2 py-1.5 text-left font-medium">Pos.</th>
                  <th className="px-2 py-1.5 text-left font-medium">LV-Text</th>
                  <th className="px-2 py-1.5 text-right font-medium">Menge</th>
                  <th className="px-2 py-1.5 text-left font-medium">Intern</th>
                  <th className="px-2 py-1.5 text-right font-medium">EK</th>
                  <th className="px-2 py-1.5 text-right font-medium">Min./Einh.</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} className="border-t border-app-border">
                    <td className="px-2 py-1 tabular-nums text-app-text-subtle">{i + 1}</td>
                    <td className="px-2 py-1 text-app-text">
                      <span className="font-medium">{r.tenderShort}</span>
                      {r.tenderLong && <span className="block text-app-text-subtle line-clamp-2">{r.tenderLong}</span>}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums whitespace-nowrap">
                      {qty.format(r.tenderQty || 0)} {r.tenderUnit}
                    </td>
                    <td className="px-2 py-1 text-app-text-muted">{r.internalShort || ''}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{r.materialPrice ? euro.format(r.materialPrice) : ''}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{r.minutesPerUnit || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
};
